import { ApiError, api } from '../../lib/api'

/**
 * The standalone system pages — `Features/Error/Endpoint.cs`,
 * `Features/Main/{Unauthorized,Shutdown}/Endpoint.cs`.
 *
 *   GET /api/error            -> { IsArabic, Title, Subtitle, ErrorMessage, BackUrl, BackButtonText }
 *   GET /api/error/{code}     -> the same, worded for that status
 *   GET /api/unauthorized     -> { IsArabic, Title, Message, HomeButtonText }
 *   GET /api/shutdown         -> { …, IsSettingsManager, LicenseUpdateLinkText, LogoutButtonText }
 *
 * These pages are ENTIRELY server-composed text — a title, a sentence or two, a button
 * label — and one of them interpolates the installation's own application title. So the
 * words are fetched rather than bundled (W6), and this app keeps its copies only as the
 * fallback for the moment before the answer arrives, or when it cannot be had at all.
 *
 * That fallback matters more here than anywhere else in the product: these are the screens
 * shown when something has already gone wrong, and an error page that cannot render
 * because it could not reach the server is no error page.
 */

/** Shutdown is the only one behind a guard, and 401 is a normal answer for it. */
async function fetchQuietly(path, signal) {
  try {
    return await api.get(path, { signal, anonymousOk: true })
  } catch (error) {
    if (error instanceof ApiError) return null
    throw error
  }
}

export async function getErrorPage(statusCode, signal) {
  const dto = await fetchQuietly(statusCode ? `/api/error/${statusCode}` : '/api/error', signal)
  if (!dto) return null

  return {
    title: dto.Title ?? '',
    // The error page is the one with a subtitle above the detail.
    lines: [dto.Subtitle, dto.ErrorMessage].filter(Boolean),
    backLabel: dto.BackButtonText ?? '',
  }
}

export async function getUnauthorizedPage(signal) {
  const dto = await fetchQuietly('/api/unauthorized', signal)
  if (!dto) return null

  return {
    title: dto.Title ?? '',
    lines: [dto.Message].filter(Boolean),
    backLabel: dto.HomeButtonText ?? '',
  }
}

export async function getShutdownPage(signal) {
  const dto = await fetchQuietly('/api/shutdown', signal)
  if (!dto) return null

  return {
    title: dto.Title ?? '',
    /* AdminMessage is the "you can fix this" note, and the server decides who sees it —
       so the role check lives there, not in a client-side copy of the rule. */
    lines: [dto.MainMessage, dto.DetailMessage, dto.IsSettingsManager ? dto.AdminMessage : null].filter(Boolean),
    isSettingsManager: Boolean(dto.IsSettingsManager),
    licenseLabel: dto.LicenseUpdateLinkText ?? '',
    logoutLabel: dto.LogoutButtonText ?? '',
  }
}

/**
 * `/Redirect` — the interstitial `ForceRedirectionMiddleware` sends a user to when their
 * profile is incomplete. It answers where to send them and how long to leave the message
 * on screen first.
 */
export async function getProfileRedirect(signal) {
  const dto = await fetchQuietly('/api/redirect', signal)
  if (!dto) return null

  return {
    title: dto.Title ?? '',
    message: dto.Message ?? '',
    /* A legacy controller name — `UserProfile`, `Courses`. Resolved through the legacy
       route map so it lands on the React screen that replaced it. */
    redirectTo: dto.RedirectUrl ?? '',
    delaySeconds: dto.RedirectDelaySeconds > 0 ? dto.RedirectDelaySeconds : 8,
  }
}
